import { Box, Stack } from '@mui/material'
import BarchartInfoLogo from 'assets/BarchartInfoLogo'
import {
	isCompleteDryMatter,
	sanitizeDryMatterInput,
} from 'components/shared/RationComponentField'
import { roundingNumber } from 'components/shared/processes'
import { useEffect, useMemo, useRef, useState } from 'react'
import RationsApiService from 'services/rationApiService'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { notifyUser } from 'store/notificationsSlice'
import {
	changeComponentInfo,
	fetchRationComponents,
} from 'store/rationSystemSlice'
import { COLORS } from 'styles/colors'
import { ButtonUI } from 'ui/buttons/ButtonUI'
import Drawer from 'ui/Drawer'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary14Bold } from 'ui/typography/TypographyPrimary14Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

type ComponentItem = {
	id: number
	title: string
	dry_matter_percent: number | string
}

type PopupEditCompOfDryPercentProps = {
	openModal: boolean
	setOpenModal: (open: boolean) => void
	components: ComponentItem[]
}

const toDryMatterInput = (value: number | string) =>
	String(value ?? '').replace(',', '.')

const PercentInput = ({
	value,
	onChange,
}: {
	value: string
	onChange: (value: string) => void
}) => {
	const [focused, setFocused] = useState(false)

	return (
		<Box
			sx={{
				width: '100px',
				height: '40px',
				flexShrink: 0,
				borderRadius: '8px',
				border: `1px solid ${focused ? COLORS.main : COLORS.inputBorder}`,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				px: '8px',
				boxSizing: 'border-box',
				backgroundColor: COLORS.white,
			}}
		>
			<Box
				component='input'
				value={value}
				autoComplete='off'
				onChange={event => {
					const nextValue = sanitizeDryMatterInput(event.target.value)
					if (nextValue !== null) onChange(nextValue)
				}}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				sx={{
					border: 'none',
					outline: 'none',
					background: 'transparent',
					padding: 0,
					width: value ? `${Math.max(value.length, 1)}ch` : '1ch',
					fontSize: '14px',
					lineHeight: '17px',
					color: COLORS.black,
					fontFamily: 'inherit',
					textAlign: 'right',
				}}
			/>
			{value && (
				<Box
					component='span'
					sx={{
						ml: '4px',
						fontSize: '14px',
						lineHeight: '17px',
						color: COLORS.black,
					}}
				>
					%
				</Box>
			)}
		</Box>
	)
}

const PopupEditCompOfDryPercent = ({
	openModal,
	setOpenModal,
	components,
}: PopupEditCompOfDryPercentProps) => {
	const dispatch = useAppDispatch()
	const rationComponents = useAppSelector(
		state => state.rationSystemReducer.rationInfo?.components,
	)
	const [values, setValues] = useState<Record<number, string>>({})
	const [initialValues, setInitialValues] = useState<Record<number, string>>({})
	const [isSaving, setIsSaving] = useState(false)
	const wasOpen = useRef(false)

	useEffect(() => {
		if (openModal && !wasOpen.current) {
			const nextValues: Record<number, string> = {}
			components?.forEach(component => {
				nextValues[component.id] = toDryMatterInput(
					component.dry_matter_percent,
				)
			})
			setValues(nextValues)
			setInitialValues(nextValues)
			setIsSaving(false)
		}
		wasOpen.current = openModal
	}, [components, openModal])

	const allComplete = (components ?? []).every(component => {
		const value = values[component.id] ?? ''
		return isCompleteDryMatter(value) && Number(value) > 0
	})
	const changedComponents = (components ?? []).filter(
		component => values[component.id] !== initialValues[component.id],
	)
	const canSave = allComplete && changedComponents.length > 0 && !isSaving

	const totalPercent = useMemo(() => {
		if (!rationComponents?.length) return null
		let drySum = 0
		let weightSum = 0
		for (const line of rationComponents) {
			const percent = values[line.component_id]
			if (!isCompleteDryMatter(percent ?? '') || Number(percent) === 0)
				return null
			const dryKg = Number(line.dry_per_head) || 0
			drySum += dryKg
			weightSum += (dryKg * 100) / Number(percent)
		}
		if (!weightSum) return null
		return (drySum / weightSum) * 100
	}, [rationComponents, values])

	const closeDrawer = () => setOpenModal(false)

	const onSubmit = async () => {
		if (!canSave) return
		setIsSaving(true)
		try {
			await Promise.all(
				changedComponents.map(component =>
					RationsApiService.patchComponent(component.id, {
						title: component.title,
						dry_matter_percent: Number(values[component.id]),
					}),
				),
			)
			changedComponents.forEach(component => {
				dispatch(
					changeComponentInfo({
						id: component.id,
						title: component.title,
						dry_matter_percent: Number(values[component.id]),
					}),
				)
			})
			dispatch(fetchRationComponents())
			if (changedComponents.length === 1) {
				const title = changedComponents[0].title
				dispatch(
					notifyUser({
						title: `СВ,% «${title}» изменен`,
						description: `В рационах, в которых использовался компонент «${title}», произведен перерасчет.`,
						isError: false,
					}),
				)
			} else {
				const titles = changedComponents
					.map(component => `«${component.title}»`)
					.join(', ')
				dispatch(
					notifyUser({
						title: `СВ,% ${titles} изменены`,
						description:
							'В рационах, в которых использовались компоненты, произведен перерасчет.',
						isError: false,
					}),
				)
			}
			closeDrawer()
		} catch {
			dispatch(
				notifyUser({
					title: 'Произошла ошибка',
					description: 'Попробуйте снова.',
					isError: true,
				}),
			)
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<Drawer
			open={openModal}
			onClose={closeDrawer}
			title='Редактирование компонента'
			anchor='right'
			width={520}
		>
			<Stack
				sx={{
					flex: 1,
					minHeight: '100%',
					px: '24px',
					boxSizing: 'border-box',
				}}
			>
				<Stack direction='row' gap='8px' alignItems='center'>
					<BarchartInfoLogo
						width={20}
						height={20}
						style={{ color: COLORS.secondaryFont, flexShrink: 0 }}
					/>
					<TypographySecondary14>
						При внесении изменений в СВ,% - в рационе, в котором используется
						компонент, автоматически будет произведен перерасчет веса
						компонента, кг/голову.
					</TypographySecondary14>
				</Stack>
				<TypographyPrimary sx={{ fontWeight: 600, mt: '24px' }}>
					Компоненты
				</TypographyPrimary>
				<Stack
					direction='row'
					justifyContent='space-between'
					sx={{
						mt: '16px',
						pb: '8px',
						borderBottom: `1px solid ${COLORS.inputBorder}`,
					}}
				>
					<TypographySecondary>Компоненты</TypographySecondary>
					<TypographySecondary sx={{ width: '100px', textAlign: 'center' }}>
						СВ, %
					</TypographySecondary>
				</Stack>
				<Stack>
					{(components ?? []).map(component => (
						<Stack
							key={component.id}
							direction='row'
							alignItems='center'
							justifyContent='space-between'
							gap='16px'
							sx={{ py: '6px' }}
						>
							<TypographyPrimary>{component.title}</TypographyPrimary>
							<PercentInput
								value={values[component.id] ?? ''}
								onChange={nextValue =>
									setValues(current => ({
										...current,
										[component.id]: nextValue,
									}))
								}
							/>
						</Stack>
					))}
				</Stack>
				<Stack
					direction='row'
					alignItems='center'
					justifyContent='space-between'
					sx={{
						mt: '8px',
						pt: '16px',
						borderTop: `1px solid ${COLORS.inputBorder}`,
					}}
				>
					<TypographyPrimary14Bold>Итого в рационе:</TypographyPrimary14Bold>
					<TypographyPrimary14Bold>
						{totalPercent == null ? '-' : roundingNumber(totalPercent, 2)} %
					</TypographyPrimary14Bold>
				</Stack>
				<Stack sx={{ mt: 'auto', pb: '24px', pt: '24px' }}>
					<ButtonUI
						disabled={!canSave}
						onClick={onSubmit}
						sx={{ height: '56px', width: '100%' }}
					>
						Сохранить
					</ButtonUI>
				</Stack>
			</Stack>
		</Drawer>
	)
}

export default PopupEditCompOfDryPercent
